import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ChakraProvider, Center, VStack, Text, Spinner, HStack, Box } from '@lifeline/ui';
import { system } from '@lifeline/ui/theme';
import { WelcomeCard, Button } from '@lifeline/ui';
import { useTranslation, initI18n } from '@lifeline/i18n';
import { createApiClient } from '@lifeline/api-client';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
  useUser,
} from '@clerk/clerk-react';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

// Create API client
const apiClient = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:8787',
});

// Health check response type
interface HealthResponse {
  ok: boolean;
  database: string;
  environment: string;
  timestamp: string;
}

function Dashboard() {
  const { t } = useTranslation();
  const { user } = useUser();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const response = await apiClient.get<HealthResponse>('/health');
      return response;
    },
    retry: false,
  });

  const status = isLoading ? 'loading' : isError ? 'error' : 'connected';

  return (
    <Center minH="100vh" bg="bg.canvas" p={4}>
      <VStack gap={6}>
        <Box position="absolute" top={4} right={4}>
          <UserButton />
        </Box>
        <WelcomeCard
          title={t('hello.title', 'Welcome to Lifeline')}
          message={
            user
              ? `${t('auth.welcome', 'Hello')}, ${user.firstName ?? user.emailAddresses[0]?.emailAddress}!`
              : t('hello.subtitle', 'Your journey begins here')
          }
          status={status}
        />
        {data && (
          <Text fontSize="sm" color="fg.muted">
            Environment: {data.environment} | Database: {data.database}
          </Text>
        )}
      </VStack>
    </Center>
  );
}

function AuthScreen() {
  const { t } = useTranslation();

  return (
    <Center minH="100vh" bg="bg.canvas" p={4}>
      <VStack gap={6}>
        <WelcomeCard
          title={t('hello.title', 'Welcome to Lifeline')}
          message={t('auth.signInPrompt', 'Sign in to start your journey')}
          status="connected"
        />
        <HStack gap={4}>
          <SignInButton mode="modal">
            <Button>{t('auth.signIn', 'Sign In')}</Button>
          </SignInButton>
          <SignUpButton mode="modal">
            <Button>{t('auth.signUp', 'Sign Up')}</Button>
          </SignUpButton>
        </HStack>
      </VStack>
    </Center>
  );
}

function AppContent() {
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    void initI18n().then(() => {
      setI18nReady(true);
    });
  }, []);

  if (!i18nReady) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <SignedIn>
        <Dashboard />
      </SignedIn>
      <SignedOut>
        <AuthScreen />
      </SignedOut>
    </>
  );
}

export function App() {
  return (
    <ChakraProvider value={system}>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ChakraProvider>
  );
}
